/**
 * Phase E1.a — Reusable CSV export state machine.
 *
 * Absorbs the boilerplate that's been duplicated across the seven
 * Download-CSV call sites the IA has shipped (StaffTab, StudentsTab,
 * ParentsTab, AdmissionsPage, AdmissionsTrashPage, AdmissionsWorkloadPage,
 * AdmissionsCalendarPage). The hook owns:
 *
 *   - `isExporting` / `exportError` state.
 *   - `try` / `catch` / `finally` around the build + download flow.
 *   - Pinning `now` exactly once at click-time so a single export sees a
 *     single clock instant (filename + bucket / retention math both
 *     derive from the same `Date`).
 *   - Canonical `<surface>-<tenant_slug>-<YYYY-MM-DD>.csv` filename via
 *     `buildRosterCsvFilename`.
 *   - The shared `buildCsv` + `downloadCsv` invocation.
 *
 * What stays at the page level (intentionally — these are the parts that
 * actually differ across the 7 sites):
 *
 *   - The `buildRows(now)` callback — owns the data composition. May be
 *     synchronous (single-shot transform of already-cached data, as on
 *     Workload / Calendar) or asynchronous (iterative paged fetch loop,
 *     as on Staff / Students / Parents / Admissions / Trash).
 *   - The disabled-state precondition (`disabled` boolean from outside).
 *   - The button JSX (variant / size / loading copy / data-testid /
 *     aria-label / icon-pulse) — those differ enough across pages that
 *     centralising them costs more than it saves.
 *   - Where the error banner sits in the DOM tree — pages keep ownership
 *     by rendering `<ExportCsvErrorBanner>` (or any custom alert) using
 *     the `exportError` value from this hook.
 *
 * Tests cover happy path, error path, re-entrancy guard, and the
 * `now`-pinning contract.
 */

import * as React from "react";

import { useTerms } from "@/hooks/useTerms";
import type { CsvCellValue } from "@/lib/csv";
import { buildCsv, buildRosterCsvFilename, downloadCsv } from "@/lib/csv";
import { exportHeader } from "@/lib/verticalProfile";

/**
 * Result returned by the page-supplied `buildRows` callback. The hook
 * never inspects the row contents — it only forwards them to `buildCsv`
 * verbatim.
 */
export interface CsvExportPayload {
  columns: ReadonlyArray<string>;
  rows: ReadonlyArray<ReadonlyArray<CsvCellValue>>;
}

/**
 * Page-supplied callback that produces the CSV payload for a single
 * export. Receives the click-time `now` so iterative loops / row
 * mappers can pin their derived values (retention countdown, bucket
 * label, etc.) to the same instant the filename uses.
 *
 * May return synchronously (single-shot transform of cached data) or
 * asynchronously (iterative paged fetch loop). The hook awaits the
 * promise either way.
 */
export type CsvExportRowsBuilder = (now: Date) => CsvExportPayload | Promise<CsvExportPayload>;

export interface UseCsvExportOptions {
  /** First segment of the canonical filename (e.g. `admissions-calendar`). */
  filenamePrefix: string;
  /** Active tenant slug; falls back to literal `tenant` when null. */
  tenantSlug: string | null | undefined;
  /** Page-owned row + column composer; called once per click. */
  buildRows: CsvExportRowsBuilder;
  /**
   * Page-owned disabled flag (empty rows, loading state, etc.). When
   * `true`, `handleExport` is a no-op so misclicks during loading
   * don't blow up.
   */
  disabled?: boolean;
  /**
   * Optional friendly fallback when the thrown error has no `.message`.
   * Defaults to `Couldn't build the CSV.` — matches the existing
   * Workload / Calendar copy. Ignored when `formatError` is supplied.
   */
  fallbackErrorMessage?: string;
  /**
   * Optional prefix applied to every error message before the
   * underlying detail. Defaults to `We couldn't generate that file. `
   * — matches the existing Workload / Calendar copy. Ignored when
   * `formatError` is supplied.
   */
  errorMessagePrefix?: string;
  /**
   * Phase D2.f — Optional page-owned formatter for the error message
   * stored in `exportError`. When provided, the hook calls this with
   * the raw thrown error and uses the returned string verbatim,
   * bypassing the default `${errorMessagePrefix}${detail}` format.
   * Lets pages preserve bespoke shapes (e.g. the colon-vs-period
   * shape used by the four toolbar pages) without forcing the hook
   * to grow a stringly-typed mini-DSL.
   */
  formatError?: (error: unknown) => string;
  /**
   * Phase D2.f — Optional external error sink. When provided, the
   * hook routes thrown errors to this callback (with the raw error
   * object) instead of tracking them internally. Useful when a page
   * wants to merge CSV errors into a shared error channel (e.g.
   * StaffTab's `staff-action-error` banner, which is shared across
   * Resend/Revoke/Suspend/Reactivate mutations and CSV export). When
   * set, the hook's internal `exportError` is always `null` and
   * `dismissError` is a no-op — the page owns both the message and
   * the dismiss UI.
   */
  onError?: (error: unknown) => void;
}

export interface UseCsvExportApi {
  /** `true` while a click is in flight. */
  isExporting: boolean;
  /** Last error message, or `null` once dismissed / no error fired. */
  exportError: string | null;
  /** Imperative dismiss for the error banner. */
  dismissError: () => void;
  /** Click handler — wire to the button's `onClick`. */
  handleExport: () => Promise<void>;
}

const DEFAULT_FALLBACK_ERROR_MESSAGE = "Couldn't build the CSV.";
const DEFAULT_ERROR_MESSAGE_PREFIX = "We couldn't generate that file. ";

/**
 * React hook that owns the CSV-export lifecycle for a single page.
 *
 * Usage:
 *
 * ```ts
 * const { isExporting, exportError, dismissError, handleExport } = useCsvExport({
 *   filenamePrefix: "admissions-calendar",
 *   tenantSlug: user?.tenant_id ?? null,
 *   disabled: !canExport,
 *   buildRows: (now) => ({
 *     columns: ADMISSIONS_CALENDAR_CSV_COLUMNS,
 *     rows: items.map((i) => mapCalendarItemToCsvRow(i, { stageMap, rosterMap, now })),
 *   }),
 * });
 * ```
 */
export function useCsvExport(options: UseCsvExportOptions): UseCsvExportApi {
  const {
    filenamePrefix,
    tenantSlug,
    buildRows,
    disabled = false,
    fallbackErrorMessage = DEFAULT_FALLBACK_ERROR_MESSAGE,
    errorMessagePrefix = DEFAULT_ERROR_MESSAGE_PREFIX,
    formatError,
    onError,
  } = options;

  const { vertical } = useTerms();
  const verticalRef = React.useRef(vertical);
  verticalRef.current = vertical;

  const [isExporting, setIsExporting] = React.useState(false);
  const [exportError, setExportError] = React.useState<string | null>(null);

  // Stable refs for the latest props so the returned `handleExport`
  // identity doesn't churn on every render of the parent — pages
  // memoise the handler downstream and we don't want to invalidate
  // their useCallback closures unnecessarily.
  const buildRowsRef = React.useRef(buildRows);
  const filenamePrefixRef = React.useRef(filenamePrefix);
  const tenantSlugRef = React.useRef(tenantSlug);
  const fallbackRef = React.useRef(fallbackErrorMessage);
  const prefixRef = React.useRef(errorMessagePrefix);
  const formatErrorRef = React.useRef(formatError);
  const onErrorRef = React.useRef(onError);
  React.useEffect(() => {
    buildRowsRef.current = buildRows;
    filenamePrefixRef.current = filenamePrefix;
    tenantSlugRef.current = tenantSlug;
    fallbackRef.current = fallbackErrorMessage;
    prefixRef.current = errorMessagePrefix;
    formatErrorRef.current = formatError;
    onErrorRef.current = onError;
  }, [
    buildRows,
    filenamePrefix,
    tenantSlug,
    fallbackErrorMessage,
    errorMessagePrefix,
    formatError,
    onError,
  ]);

  const dismissError = React.useCallback(() => {
    // When `onError` is set, the page owns both message storage and
    // dismissal — there's nothing for us to clear.
    if (onErrorRef.current) return;
    setExportError(null);
  }, []);

  const handleExport = React.useCallback(async () => {
    // Re-entrancy + disabled guards. Pages compute their own `disabled`
    // (which already folds in `isExporting`), but a second click between
    // the React state flush and the next render could still slip
    // through; the explicit `isExporting` check below is the
    // belt-and-braces guard for that race.
    if (disabled) return;
    setIsExporting((prev) => {
      if (prev) return prev;
      return true;
    });
    // Only clear internal state when we own it — otherwise the page
    // already owns the channel (and may want to keep an unrelated
    // error visible, e.g. StaffTab's last suspend failure shouldn't
    // get wiped by clicking Download CSV).
    if (!onErrorRef.current) setExportError(null);
    try {
      const now = new Date();
      const payload = await buildRowsRef.current(now);
      const columns = payload.columns.map((column) => exportHeader(verticalRef.current, column));
      const csv = buildCsv(columns, payload.rows);
      const filename = buildRosterCsvFilename(
        filenamePrefixRef.current,
        tenantSlugRef.current,
        now,
      );
      downloadCsv(filename, csv);
    } catch (err) {
      // `onError` wins outright: page owns the channel, hook stays out.
      if (onErrorRef.current) {
        onErrorRef.current(err);
      } else if (formatErrorRef.current) {
        setExportError(formatErrorRef.current(err));
      } else {
        const detail = err instanceof Error && err.message ? err.message : fallbackRef.current;
        setExportError(`${prefixRef.current}${detail}`);
      }
    } finally {
      setIsExporting(false);
    }
  }, [disabled]);

  return { isExporting, exportError, dismissError, handleExport };
}
